import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ScrollView,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
} from 'react-native';
import type { V2DepositAccount, V2HistoryRecord, V2TransactionType } from '../../types/v2';
import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import { COLORS } from '../../theme/themeTokens';
import {
  v2GetAccountDepositView,
  v2GetMyBalances,
  v2GetMyHistory,
  v2GetTransactionTypes,
  v2TransactionTypeIconUrl,
} from '../../services/v2/api';
import { useAuth } from '../../contexts/AuthContext';
import TokenIcon from '../../components/UIComponents/TokenIcon';
import { formatSignedAmount, signedAmountStyle } from '../../utils/transactionSign';
import { isConfirmedStatus } from '../../utils/transactionStatus';
import { formatDepositMeta } from '../../utils/depositMeta';

// ── helpers ──────────────────────────────────────────────────────────────────

const parseAmount = (s: string | null | undefined): number => {
  const n = parseFloat(s ?? '');
  return isNaN(n) ? 0 : n;
};

/** "1 234 567,89" — locale-agnostic thousands separator + comma decimal. */
const withSpaces = (num: number, decimals = 2): string => {
  const [int, dec] = Math.abs(num).toFixed(decimals).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + dec;
};

/** "123 456" — integer part only (used for the big balance figure). */
const formatBigAmount = (amount: string): { whole: string; fraction: string } => {
  const num = parseFloat(amount);
  if (isNaN(num)) return { whole: '0', fraction: '00' };
  const fixed = Math.abs(num).toFixed(2);
  const [int, dec] = fixed.split('.');
  return {
    whole: int.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    fraction: dec,
  };
};

/** "DD.MM.YY" — used in the term range row. */
const formatShortDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear().toString().slice(-2);
  return `${day}.${month}.${year}`;
};

/** "14.04.2026" — full date used as group header in History list. */
const formatFullDate = (dateString: string): string => {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

/** Group records by date key (dd.MM.yyyy) preserving order, sorted desc. */
const groupByDate = (
  items: V2HistoryRecord[],
): { date: string; items: V2HistoryRecord[] }[] => {
  // Primary date DESC + secondary sort_order DESC — pair-row'ы
  // (debit baseSort+1, credit baseSort+2) показывают credit сверху.
  // Зеркало TransactionHistoryScreen.groupByDate.
  const sorted = [...items].sort((a, b) => {
    const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateDiff !== 0) return dateDiff;
    return (b.sort_order ?? 0) - (a.sort_order ?? 0);
  });
  const map = new Map<string, V2HistoryRecord[]>();
  for (const rec of sorted) {
    const key = formatFullDate(rec.date);
    const group = map.get(key);
    if (group) group.push(rec);
    else map.set(key, [rec]);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
};

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// formatAmount + sign visuals moved to src/utils/transactionSign so the
// "catalog-only" rule (no numeric fallback) is enforced by a single
// source across history lists. Import is added at the top of the file.

/** Same normalisation as BalanceDetailScreen / TransactionHistoryScreen — keeps
 *  the icon lookup resilient to backend name drift between catalog rows and
 *  history-record snapshots. */
const normalizeTypeName = (name: string | null | undefined): string => {
  const lower = (name || '').toLowerCase().trim();
  if (lower.includes('withdr')) return 'withdraw';
  if (lower.includes('depos') || lower.includes('inflow')) return 'deposit';
  return lower;
};

// ── Transaction Item ─────────────────────────────────────────────────────────

const TransactionItem: React.FC<{
  item: V2HistoryRecord;
  typeIconUri: string | null;
  amountSign: V2TransactionType['amount_sign'] | null;
}> = ({ item, typeIconUri, amountSign }) => {
  const formattedAmount = formatSignedAmount(item.amount, amountSign);
  const { color: amountColor } = signedAmountStyle(amountSign);

  return (
    <View style={styles.txRow}>
      <View style={styles.txIconWrap}>
        {typeIconUri ? (
          <Image source={{ uri: typeIconUri }} style={styles.txIcon} resizeMode="contain" />
        ) : (
          <View style={[styles.txIcon, styles.txIconPlaceholder]}>
            <Text style={styles.txIconPlaceholderText}>
              {capitalize(item.transaction_type_name).charAt(0) || '?'}
            </Text>
          </View>
        )}
      </View>
      <Text style={styles.txTypeName} numberOfLines={1}>
        {capitalize(item.transaction_type_name)}
      </Text>
      <Text
        style={[styles.txAmount, { color: amountColor }]}
        numberOfLines={1}
      >
        {formattedAmount}{' '}
        <Text style={styles.txCurrency}>{item.currency}</Text>
      </Text>
    </View>
  );
};

// ── Screen Props ─────────────────────────────────────────────────────────────

interface Props {
  /** Investment account id. Drives the canonical
   *  `GET /accounts/:id/deposit-view` aggregate read, which works for
   *  both new deposits (term/apy/compounding populated from migration
   *  00080 snapshot) and legacy investment accounts (those fields
   *  come back empty so the UI renders `—`). */
  accountId: string;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** Called when the user taps "Open new" in the action row. Parent
   *  routes to OpenDepositScreen (and is responsible for navigating
   *  back here on screen-close). */
  onOpenNewDeposit?: () => void;
  /** Called when the user taps "Inflow" in the action row. Parent
   *  routes to TopUpDepositScreen passing the current accountId. */
  onTopUp?: () => void;
  /** Called when the user taps "Close" in the action row. Parent
   *  routes to CloseDepositScreen passing the current accountId. */
  onClose?: () => void;
  /** Called when the user taps "Withdraw" in the action row. Parent
   *  routes to OutflowDepositScreen passing the current accountId. */
  onWithdraw?: () => void;
}

// ── Screen ───────────────────────────────────────────────────────────────────

const DepositAccountScreen: React.FC<Props> = ({ accountId, onBack, onNavigate, onOpenNewDeposit, onTopUp, onClose, onWithdraw }) => {
  const { isAuthenticated, isImpersonating } = useAuth();
  // Под impersonation backend hard-deny'ит все POST'ы на deposit'ы (open /
  // topup / close / outflow). Preemptively disable кнопки 4-action row'а
  // чтобы support staff не тыкал в них впустую и не получал 403.
  const disableWrites = isImpersonating;
  // Status-bar inset for Android — matches CardScreen / BalanceDetail
  // so the title lines up vertically with every other in-app screen.
  // iOS gets 0 (App-level SafeAreaView handles the notch).
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;

  const [account, setAccount] = useState<V2DepositAccount | null>(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState<string | null>(null);

  // Pending close-заявка → весь investment balance залочен (backend
  // CreateAccountClose делает LockAmount(investBalance.Amount)). Ищем
  // balance по account_id и проверяем available_amount==0. Если да —
  // disable кнопки Inflow (Top-up) и Withdraw: транзакции по уходящему
  // в закрытие депозиту бессмысленны и на backend'e упадут с
  // insufficient_funds.
  const [closePending, setClosePending] = useState(false);

  const [history, setHistory] = useState<V2HistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [transactionTypes, setTransactionTypes] = useState<V2TransactionType[]>([]);

  // Two catalog maps — see BalanceDetailScreen for the rationale.
  // amount_sign must be looked up by EXACT name (each type carries
  // its own sign), while icons can collapse via normalizeTypeName
  // because multiple types share an icon family.
  const signByExactName = useMemo(() => {
    const m = new Map<string, V2TransactionType['amount_sign']>();
    for (const t of transactionTypes) m.set(t.name, t.amount_sign);
    return m;
  }, [transactionTypes]);
  const iconByExactName = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) m.set(t.name, v2TransactionTypeIconUrl(t));
    return m;
  }, [transactionTypes]);
  const iconByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) {
      const key = normalizeTypeName(t.name);
      const iconUri = v2TransactionTypeIconUrl(t);
      if (!m.has(key) || (m.get(key) == null && iconUri != null)) {
        m.set(key, iconUri);
      }
    }
    return m;
  }, [transactionTypes]);

  // Fetch the aggregate account.
  const fetchAccount = useCallback(async () => {
    if (!isAuthenticated) return;
    setAccountError(null);
    try {
      const data = await v2GetAccountDepositView(accountId);
      // Защиту "self-bounce на is_closed=true" убрали — backend
      // ListMyBalances уже фильтрует закрытые depositаккаунты через
      // FindClosedInvestmentAccountIDs, поэтому tile'ы для них не
      // показываются. Если каким-то edge-case'ом юзер попал на screen
      // закрытого депозита — покажем доступные данные, не блокируем
      // navigation. Это убирает false-positive bounce'ы для активных
      // деpoзитов с pending/cancelled close-заявкой (где is_closed
      // остаётся false но frontend всё равно bounce'ил из-за прошлой
      // зашитой логики).
      setAccount(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load deposit account';
      setAccountError(msg);
    } finally {
      setAccountLoading(false);
    }
  }, [isAuthenticated, accountId, onBack]);

  // Fetch history filtered by investment_balance_id (once we know it from the
  // aggregate response). Falling back to "no history" is fine until the
  // backend returns a balance id (deposits opened before openInvestmentAccount
  // shipped have a nil balance and an empty timeline).
  const fetchHistory = useCallback(async (balanceID: string) => {
    setHistoryLoading(true);
    try {
      const res = await v2GetMyHistory({ balance_id: balanceID });
      // History list shows only fully-settled rows. Pending /
      // in_process / cancelled applications stay invisible. BC-aware
      // helper accepts legacy `completed` / `success` too.
      const confirmed = (res.records ?? []).filter((r) =>
        isConfirmedStatus(r.transaction_status_name),
      );
      setHistory(confirmed);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await v2GetTransactionTypes();
      setTransactionTypes(res.transaction_types ?? []);
    } catch {
      setTransactionTypes([]);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchTypes();
  }, [fetchAccount, fetchTypes]);

  // Ищем investment balance для текущего аккаунта и определяем
  // close-pending state (available_amount == 0 при amount > 0). Отдельный
  // effect с deps=[accountId] — перезапускается при переключении на
  // другой депозит без ре-mount'а screen'а.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetMyBalances();
        if (cancelled) return;
        const bal = (res?.balances ?? []).find(
          (b) => b.account_id === accountId,
        );
        if (!bal) {
          setClosePending(false);
          return;
        }
        const amount = parseFloat(bal.amount ?? '0');
        const available = parseFloat(bal.available_amount ?? bal.amount ?? '0');
        setClosePending(amount > 0 && available <= 0);
      } catch (err) {
        console.warn('[DepositAccount] Failed to check close-pending state:', err);
        if (!cancelled) setClosePending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (account?.investment_balance_id) {
      fetchHistory(account.investment_balance_id);
    } else {
      setHistoryLoading(false);
    }
  }, [account?.investment_balance_id, fetchHistory]);

  const currency = account?.symbol || account?.currency || '';
  // APY and Term may be empty on legacy investment accounts (snapshot
  // columns added in migration 00080 stay NULL for pre-existing rows).
  // Unified caption via formatDepositMeta — drops missing fields, so
  // legacy rows render as "USDT" alone or "USDT · 6 months" without
  // the "—" placeholders.
  const apyFraction = account ? parseFloat(account.apy) : NaN;
  const metaText = formatDepositMeta(
    currency,
    account?.term ?? null,
    Number.isFinite(apyFraction) ? apyFraction : null,
  ) || currency || '—';
  const balance = formatBigAmount(account?.current_balance ?? '0');
  const accrued = formatSignedAmount(account?.accrued_interest ?? '0', 'positive');
  const termRange = account && account.deposit_date_from && account.deposit_date_to
    ? `${formatShortDate(account.deposit_date_from)} – ${formatShortDate(account.deposit_date_to)}`
    : '—';

  // 30-дневное окно снятия — вся сигнализация про правило теперь живёт
  // на OutflowDepositScreen (picker показывает closed-window depositы
  // как disabled + amber warning + backend guard). DepositAccountScreen
  // Withdraw-кнопку не блокирует — юзер всегда может зайти на экран
  // и получить понятное объяснение.

  return (
    <View style={styles.root}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      {/* Uniform 20 dp vertical rhythm — header wrapper compensates
          the per-platform paddingBottom inside SimpleHeader so the
          gap from title to the top card is exactly 20 on both iOS
          and Android. */}
      <View style={{
        paddingTop: statusBarHeight,
        paddingBottom: Platform.OS === 'android' ? 12 : 0,
        backgroundColor: COLORS.primaryNavy,
      }}>
        <SimpleHeader
          title="Deposit account"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      </View>

      {/* Top card on navy strip — token meta, big balance, accrued interest, term. */}
      {accountLoading ? (
        <View style={styles.topCardLoading}>
          <ActivityIndicator size="small" color={COLORS.white} />
        </View>
      ) : accountError ? (
        <View style={styles.topCardError}>
          <Text style={styles.errorText}>{accountError}</Text>
        </View>
      ) : (
        <View style={styles.topCard}>
          {/* Token meta row: icon + "USDT · 6 months · 4%" */}
          <View style={styles.metaRow}>
            <TokenIcon
              symbol={currency}
              iconUrl={account?.icon_url ?? null}
              style={styles.tokenIcon}/>
            <Text style={styles.metaText}>
              {metaText}
            </Text>
          </View>

          {/* Big balance figure */}
          <View style={styles.balanceRow}>
            <Text style={styles.balanceWhole}>{balance.whole}</Text>
            <Text style={styles.balanceFraction}>,{balance.fraction}</Text>
            <Text style={styles.balanceCurrency}> {currency}</Text>
          </View>

          {/* Bottom split: Accrued interest / Term */}
          <View style={styles.splitRow}>
            <View style={styles.splitCol}>
              <Text style={styles.splitLabel}>Accrued interest</Text>
              <Text style={styles.splitValueAccrued}>{accrued} {currency}</Text>
            </View>
            <View style={styles.splitCol}>
              <Text style={styles.splitLabel}>Term</Text>
              <Text style={styles.splitValue}>{termRange}</Text>
            </View>
          </View>
        </View>
      )}

      {/* Action row — 4 buttons. Stubs for now; wired up later. */}
      <View style={styles.actionRow}>
        <View style={styles.actionItem}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnInflow, (closePending || disableWrites) && styles.actionBtnDisabled]}
            activeOpacity={0.75}
            onPress={onTopUp}
            disabled={!onTopUp || closePending || disableWrites}
          >
            <Image source={require('../../assets/arrowup.png')} style={styles.actionIcon} resizeMode="contain" />
          </TouchableOpacity>
          <Text style={styles.actionLabel}>Inflow</Text>
        </View>
        <View style={styles.actionItem}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnNavy, (closePending || disableWrites) && styles.actionBtnDisabled]}
            activeOpacity={0.75}
            onPress={onWithdraw}
            disabled={!onWithdraw || closePending || disableWrites}
          >
            <Image source={require('../../assets/withdraw.png')} style={styles.actionIcon} resizeMode="contain" />
          </TouchableOpacity>
          <Text style={styles.actionLabel}>Withdraw</Text>
        </View>
        <View style={styles.actionItem}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnNavy, (closePending || disableWrites) && styles.actionBtnDisabled]}
            activeOpacity={0.75}
            onPress={onClose}
            disabled={!onClose || closePending || disableWrites}
          >
            <Image source={require('../../assets/cross.png')} style={styles.actionIcon} resizeMode="contain" />
          </TouchableOpacity>
          <Text style={styles.actionLabel}>Close</Text>
        </View>
        <View style={styles.actionItem}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnNavy, disableWrites && styles.actionBtnDisabled]}
            activeOpacity={0.75}
            onPress={onOpenNewDeposit}
            disabled={!onOpenNewDeposit || disableWrites}
          >
            <Image source={require('../../assets/plus.png')} style={styles.actionIcon} resizeMode="contain" />
          </TouchableOpacity>
          <Text style={styles.actionLabel}>Open new</Text>
        </View>
      </View>

      {/* History — rounded pureLight body, same pattern as BalanceDetailScreen. */}
      <ScrollView
        style={styles.historyScroll}
        contentContainerStyle={styles.historyContent}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.historyTitle}>History</Text>
        {historyLoading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={COLORS.primaryNavy} />
          </View>
        ) : history.length === 0 ? (
          <View style={styles.emptyRow}>
            <Text style={styles.emptyText}>No transactions yet</Text>
          </View>
        ) : (
          groupByDate(history).map((group) => (
            <View key={group.date}>
              <Text style={styles.dateHeader}>{group.date}</Text>
              {group.items.map((item) => {
                const name = item.transaction_type_name;
                const amountSign = signByExactName.get(name) ?? null;
                const iconUri =
                  iconByExactName.get(name) ??
                  iconByCategory.get(normalizeTypeName(name)) ??
                  null;
                return (
                  <TransactionItem
                    key={item.id}
                    item={item}
                    typeIconUri={iconUri}
                    amountSign={amountSign}
                  />
                );
              })}
            </View>
          ))
        )}
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate as never} />
    </View>
  );
};

export default DepositAccountScreen;

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  topCardLoading: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  topCardError: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  errorText: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.white,
    opacity: 0.8,
    textAlign: 'center',
  },
  /* Top card — sits on the navy strip directly under SimpleHeader. Uses a
     subtle white-tint overlay so it reads as a card against the navy bg.
     marginTop: 0 — the header wrapper above already owns the 20 dp gap
     to the title (paddingBottom inside SimpleHeader + Android pad). */
  topCard: {
    marginHorizontal: 16,
    marginTop: 0,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    // 20 dp gap to the balance row, matching the screen rhythm.
    marginBottom: 20,
  },
  tokenIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  metaText: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    // 20 dp gap to the bottom split row.
    marginBottom: 20,
  },
  balanceWhole: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: '500',
    color: COLORS.white,
    lineHeight: 40,
  },
  balanceFraction: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '400',
    color: COLORS.white,
    opacity: 0.85,
    lineHeight: 26,
  },
  balanceCurrency: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: '400',
    color: COLORS.white,
    opacity: 0.7,
    marginLeft: 4,
  },
  splitRow: {
    flexDirection: 'row',
  },
  splitCol: {
    flex: 1,
  },
  splitLabel: {
    fontFamily: 'Inter',
    fontSize: 13,
    color: COLORS.white,
    opacity: 0.7,
    marginBottom: 4,
  },
  splitValue: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
  },
  splitValueAccrued: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: '#5EC07E',
  },

  /* Action row — 4 buttons (Inflow / Withdraw / Close / Open new). Inflow
     uses brandOrange like Dashboard's row; the other three sit on a darker
     navy fill so they read as "secondary" actions. */
  actionRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    // 20 dp top + 20 dp bottom keep the row symmetric: 20 to the top
    // card above, and 20 of navy below the labels before the pureLight
    // sheet boundary. The 20 inside the sheet (historyContent.paddingTop)
    // then keeps the "History" title 20 off the rounded edge.
    paddingTop: 20,
    paddingBottom: 20,
    // 20 dp between the 4 columns — matches the screen's global
    // vertical-rhythm step and gives the rectangular buttons more
    // breathing room than the previous 12.
    gap: 20,
    backgroundColor: COLORS.primaryNavy,
  },
  actionItem: {
    flex: 1,
    alignItems: 'center',
  },
  actionBtn: {
    // Rectangular: width spans the whole flex column, height stays
    // 60 dp (same as the dashboard's AccountActionRow). Reverts the
    // earlier 60×60 square — design call to bring the strip back to
    // the original wide-button look.
    alignSelf: 'stretch',
    height: 60,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  actionBtnInflow: {
    backgroundColor: COLORS.brandOrange,
  },
  actionBtnNavy: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  actionBtnDisabled: {
    opacity: 0.4,
  },
  actionIcon: {
    // 28x28 matches the dashboard's AccountActionRow icons so the
    // four-button strip here reads at the same visual weight as the
    // top-level Inflow / Withdraw / Top-up trio.
    width: 28,
    height: 28,
    tintColor: COLORS.white,
  },
  actionLabel: {
    fontFamily: 'Inter',
    fontSize: 13,
    color: COLORS.white,
    textAlign: 'center',
  },

  /* History scroll area — pureLight body with rounded top corners. */
  historyScroll: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  historyContent: {
    // 20 dp owns the gap from the action row to the "History" title;
    // the 20 dp button → label margin from actionBtn already sits
    // inside the navy strip above.
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 140,
  },
  historyTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    // 20 dp gap to the first history row, matching the rhythm.
    marginBottom: 20,
  },
  loadingRow: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyRow: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.blueSteel,
  },
  dateHeader: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.blueSteel,
    marginTop: 16,
    marginBottom: 8,
  },

  /* Transaction row */
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  txIconWrap: {
    width: 36,
    height: 36,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  txIcon: {
    width: 36,
    height: 36,
  },
  txIconPlaceholder: {
    backgroundColor: COLORS.softGrey,
    borderRadius: 8,
  },
  txIconPlaceholderText: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  txTypeName: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  txAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
  },
  txAmountPositive: {
    color: '#5EC07E',
  },
  txAmountNegative: {
    color: COLORS.textPrimary,
  },
  txCurrency: {
    // No `color` here — the nested Text inherits the parent's
    // amountColor (red/green/default), keeping the value and the
    // currency suffix in the same colour per design.
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
  },
});
